import { Epic, ofType } from 'redux-observable';
import { EMPTY, Observable, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { Action } from '../actions';
import {
    ACCEPT_CAMPAIGN_AND_GROUP_SELECTOR_DATA,
    ACCEPT_CAMPAIGN_AND_GROUP_SELECTOR_DATA_DNA,
    changeCampaignsAndGroupSelectorStatus,
    FETCH_CAMPAIGN_AND_GROUP_SELECTOR_CAMPAIGNS_DATA,
    FETCH_CAMPAIGN_AND_GROUP_SELECTOR_GROUPS_DATA,
    fetchCampaignAndGroupSelectorCampaignsData,
    FetchCampaignAndGroupSelectorCampaignsData,
    fetchCampaignAndGroupSelectorGroupsData,
    FetchCampaignAndGroupSelectorGroupsData,
    OPEN_CAMPAIGN_AND_GROUP_SELECTOR,
    SELECT_CAMPAIGN_AND_GROUP_SELECTOR_CAMPAIGN,
    SelectCampaignAndGroupSelectorCampaign,
    selectCampaignAndGroupSelectorCampaign,
    selectCampaignAndGroupSelectorGroup,
    updateCampaignAndGroupSelectorCampaignsData,
    updateCampaignAndGroupSelectorGroupsData
} from '../actions/campaignAndGroupSelector';
import { closeSidebarMenus, DummyAction, dummyAction } from '../actions/common';
import {
    addEntity,
    goToBannersEditForCreation,
    GoToBannersEditForCreationAction,
    goToGroupsEditForCreation,
    GoToGroupsEditForCreationAction,
    goToInternalBannersEditForCreation,
    GoToInternalBannersEditForCreationAction
} from '../actions/grid-screen';
import {
    createTextAdGroup,
    CreateTextAdGroupAction
} from '../actions/groupsEdit';
import { openPhrasesSelectorWizard } from '../actions/phrasesSelectorWizard';
import { SelectorStatus } from '../components/CampaignAndGroupSelector/CampaignAndGroupSelector';
import { SelectorItems } from '../components/CampaignAndGroupSelector/Selector/Selector';
import { getNewGroupName } from '../mappers/group/group';
import { selectGroupsSuggestListFilter } from '../selectors/campaignAndGroupSelector';
import {
    selectPreselectedCampaign,
    selectPreselectedGroup
} from '../selectors/grid/grid';
import {
    selectAddableGroupTypes,
    selectCampaignTypesForBannerCreation,
    selectCampaignTypesForDnaBannerCreation
} from '../selectors/groupEdit/group/group';
import '../service/Rx';
import { EpicDeps } from '../service/StoreFactory';
import { CampaignType } from '../typings/domain';
import { GridEditedEntityType } from '../typings/grid';
import { PhrasesSelectorWizardId } from '../typings/phrases';
import {
    CampaignAndGroupSelectorEntityToSelectType,
    State
} from '../typings/state';

const fetchCampaignAndGroupSelectorCampaignsDataEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    const requestAction$ = action$
        .ofType(FETCH_CAMPAIGN_AND_GROUP_SELECTOR_CAMPAIGNS_DATA)
        .debounceTime(500)
        .mergeMap((action: FetchCampaignAndGroupSelectorCampaignsData) => {
            const {
                commonData: { login }
            } = store$.value;

            return Observable.fromPromise(
                deps.entitiesDataProvider.fetchCampaignsSuggestList(
                    login,
                    action.filter
                )
            )
                .mergeMap((data: SelectorItems[]) => {
                    const preselectedCampaign = selectPreselectedCampaign(
                        store$.value,
                        data
                    );

                    return Observable.concat(
                        Observable.of(
                            updateCampaignAndGroupSelectorCampaignsData(data)
                        ),
                        Observable.of(
                            selectCampaignAndGroupSelectorCampaign(
                                preselectedCampaign
                            )
                        ),
                        Observable.of(
                            changeCampaignsAndGroupSelectorStatus(
                                SelectorStatus.READY
                            )
                        )
                    );
                })
                .catch(() =>
                    Observable.of(
                        changeCampaignsAndGroupSelectorStatus(
                            SelectorStatus.CAMPAIGN_ERROR
                        )
                    )
                );
        });

    const resetAction$ = action$
        .ofType(FETCH_CAMPAIGN_AND_GROUP_SELECTOR_CAMPAIGNS_DATA)
        .filter(() => {
            const {
                grid: { campaignAndGroupSelector }
            } = store$.value;
            const { selectorStatus } = campaignAndGroupSelector;

            return selectorStatus !== SelectorStatus.LOADING_CAMPAIGN;
        })
        .mergeMap((action: FetchCampaignAndGroupSelectorCampaignsData) => {
            return Observable.of(
                updateCampaignAndGroupSelectorCampaignsData([])
            ).merge([
                selectCampaignAndGroupSelectorCampaign(undefined),
                updateCampaignAndGroupSelectorGroupsData([]),
                changeCampaignsAndGroupSelectorStatus(
                    SelectorStatus.LOADING_CAMPAIGN
                ),
                selectCampaignAndGroupSelectorGroup(undefined)
            ]);
        });

    return resetAction$.merge(requestAction$);
};

const fetchCampaignAndGroupSelectorGroupsDataEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    const requestAction$ = action$
        .ofType(FETCH_CAMPAIGN_AND_GROUP_SELECTOR_GROUPS_DATA)
        .debounceTime(500)
        .mergeMap((action: FetchCampaignAndGroupSelectorGroupsData) => {
            const { commonData, grid } = store$.value;
            const { campaignAndGroupSelector } = grid;
            const { login } = commonData;
            const { campaignSelected } = campaignAndGroupSelector;

            if (!campaignSelected) {
                return Observable.of();
            }

            const groupSuggestFilter = selectGroupsSuggestListFilter(
                store$.value,
                action.filter,
                campaignSelected
            );

            return Observable.fromPromise(
                deps.entitiesDataProvider.fetchGroupsSuggestList(
                    login,
                    groupSuggestFilter
                )
            )
                .mergeMap((data: SelectorItems[]) => {
                    const preselectedGroup = selectPreselectedGroup(
                        store$.value,
                        data
                    );

                    return Observable.concat(
                        Observable.of(
                            updateCampaignAndGroupSelectorGroupsData(data)
                        ),
                        Observable.of(
                            selectCampaignAndGroupSelectorGroup(
                                preselectedGroup
                            )
                        ),
                        Observable.of(
                            changeCampaignsAndGroupSelectorStatus(
                                SelectorStatus.READY
                            )
                        )
                    );
                })
                .catch(() =>
                    Observable.of(
                        changeCampaignsAndGroupSelectorStatus(
                            SelectorStatus.GROUP_ERROR
                        )
                    )
                );
        });

    const resetAction$ = action$
        .ofType(FETCH_CAMPAIGN_AND_GROUP_SELECTOR_GROUPS_DATA)
        .filter(() => {
            const {
                grid: { campaignAndGroupSelector }
            } = store$.value;
            const { selectorStatus } = campaignAndGroupSelector;

            return selectorStatus !== SelectorStatus.LOADING_GROUP;
        })
        .mergeMap((action: FetchCampaignAndGroupSelectorGroupsData) => {
            return Observable.of(
                updateCampaignAndGroupSelectorGroupsData([])
            ).merge([
                changeCampaignsAndGroupSelectorStatus(
                    SelectorStatus.LOADING_GROUP
                ),
                selectCampaignAndGroupSelectorGroup(undefined)
            ]);
        });

    return resetAction$.merge(requestAction$);
};

const acceptCampaignAndGroupSelectorDataDnaEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$) => {
    return action$.pipe(
        ofType(ACCEPT_CAMPAIGN_AND_GROUP_SELECTOR_DATA_DNA),
        mergeMap(
            (): Observable<
                | CreateTextAdGroupAction
                | GoToGroupsEditForCreationAction
                | GoToBannersEditForCreationAction
                | GoToInternalBannersEditForCreationAction
                | DummyAction
            > => {
                const {
                    grid: {
                        campaignAndGroupSelector: {
                            campaignSelected,
                            groupSelected,
                            entityToSelectType
                        }
                    },
                    commonData: {
                        client: { login },
                        operatorFeatures,
                        accessRights
                    }
                } = store$.value;

                if (login === undefined) {
                    throw new Error('client login is undefined');
                }

                if (!campaignSelected) {
                    return EMPTY;
                }

                switch (entityToSelectType) {
                    case CampaignAndGroupSelectorEntityToSelectType.GROUP:
                        if (
                            selectAddableGroupTypes(
                                operatorFeatures,
                                accessRights
                            ).includes(campaignSelected.type)
                        ) {
                            switch (campaignSelected.type) {
                                case CampaignType.TEXT:
                                    return of(
                                        createTextAdGroup(
                                            campaignSelected.id,
                                            getNewGroupName()
                                        )
                                    );
                                case CampaignType.INTERNAL_FREE:
                                case CampaignType.INTERNAL_DISTRIB:
                                case CampaignType.CPM_BANNER:
                                case CampaignType.PERFORMANCE: {
                                    return of(
                                        goToGroupsEditForCreation(
                                            login,
                                            campaignSelected.id
                                        )
                                    );
                                }
                                default:
                                    return EMPTY;
                            }
                        } else {
                            throw new Error(
                                `создание групп типа ${
                                    campaignSelected.type
                                } не поддерживается`
                            );
                        }
                    case CampaignAndGroupSelectorEntityToSelectType.BANNER:
                        if (
                            selectCampaignTypesForBannerCreation(
                                operatorFeatures,
                                accessRights
                            ).includes(campaignSelected.type) ||
                            selectCampaignTypesForDnaBannerCreation(
                                operatorFeatures,
                                accessRights
                            ).includes(campaignSelected.type)
                        ) {
                            if (!groupSelected) {
                                return EMPTY;
                            } else {
                                switch (campaignSelected.type) {
                                    case CampaignType.INTERNAL_FREE:
                                    case CampaignType.INTERNAL_DISTRIB:
                                        return Observable.of(
                                            goToInternalBannersEditForCreation(
                                                login,
                                                campaignSelected.id,
                                                groupSelected.id
                                            )
                                        );
                                    case CampaignType.TEXT:
                                    case CampaignType.PERFORMANCE:
                                    case CampaignType.CPM_BANNER:
                                    case CampaignType.MCBANNER:
                                        return of(
                                            goToBannersEditForCreation(
                                                login,
                                                campaignSelected.id,
                                                groupSelected.id
                                            )
                                        );
                                    default:
                                        return EMPTY;
                                }
                            }
                        } else {
                            throw new Error(
                                `создание баннеров для кампаний типа ${
                                    campaignSelected.type
                                } не поддерживается`
                            );
                        }
                    default:
                        return EMPTY;
                }
            }
        )
    );
};

const acceptCampaignAndGroupSelectorDataEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.ofType(ACCEPT_CAMPAIGN_AND_GROUP_SELECTOR_DATA).mergeMap(
        (): Observable<Action> => {
            const {
                commonData: {
                    client: { login }
                },
                grid: {
                    campaignAndGroupSelector: {
                        campaignSelected,
                        groupSelected,
                        entityToSelectType
                    }
                }
            } = store$.value;

            if (login === undefined) {
                throw new Error('client login is undefined');
            }

            if (!campaignSelected) {
                return Observable.of();
            }

            switch (entityToSelectType) {
                case CampaignAndGroupSelectorEntityToSelectType.GROUP:
                    return Observable.of(
                        addEntity({
                            type: GridEditedEntityType.Group,
                            campaignId: campaignSelected.id,
                            campaignType: campaignSelected.type,
                            login
                        })
                    );
                    break;
                case CampaignAndGroupSelectorEntityToSelectType.BANNER:
                    if (groupSelected) {
                        return Observable.of(
                            addEntity({
                                type: GridEditedEntityType.Banner,
                                campaignId: campaignSelected.id,
                                campaignType: campaignSelected.type,
                                groupId: groupSelected ? groupSelected.id : '',
                                login
                            })
                        );
                    }
                    break;
                case CampaignAndGroupSelectorEntityToSelectType.PHRASE:
                    if (groupSelected) {
                        return Observable.of(
                            openPhrasesSelectorWizard(
                                PhrasesSelectorWizardId.sideBar,
                                {
                                    id: groupSelected ? groupSelected.id : '',
                                    name: groupSelected.name,
                                    campaign: {
                                        type: campaignSelected.type,
                                        name: campaignSelected.name,
                                        id: campaignSelected.id,
                                        strategy: {
                                            isAutoBudget: Boolean(
                                                campaignSelected.isAutoBudget
                                            )
                                        }
                                    },
                                    minusKeywords:
                                        groupSelected.minusKeywords || [],
                                    regionIds: groupSelected.regionsIds || []
                                }
                            )
                        );
                    }
                    break;
                default:
                    throw new Error(
                        'groupSelected not choosed or view type not supported'
                    );
            }

            return Observable.of();
        }
    );
};

const openCampaignAndGroupSelectorEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(OPEN_CAMPAIGN_AND_GROUP_SELECTOR),
        mergeMap(() => {
            return [
                fetchCampaignAndGroupSelectorCampaignsData(''),
                closeSidebarMenus()
            ];
        })
    );
};

const selectCampaignAndGroupSelectorCampaignEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) => {
    return action$
        .ofType(SELECT_CAMPAIGN_AND_GROUP_SELECTOR_CAMPAIGN)
        .map((action: SelectCampaignAndGroupSelectorCampaign) => {
            if (!action.campaignSelected) {
                return dummyAction;
            }

            const entityToSelectType =
                store$.value.grid.campaignAndGroupSelector.entityToSelectType;

            switch (entityToSelectType) {
                case CampaignAndGroupSelectorEntityToSelectType.BANNER:
                case CampaignAndGroupSelectorEntityToSelectType.PHRASE:
                    return fetchCampaignAndGroupSelectorGroupsData('');
                default:
                    return dummyAction;
            }
        });
};

export {
    fetchCampaignAndGroupSelectorCampaignsDataEpic,
    fetchCampaignAndGroupSelectorGroupsDataEpic,
    acceptCampaignAndGroupSelectorDataEpic,
    openCampaignAndGroupSelectorEpic,
    selectCampaignAndGroupSelectorCampaignEpic,
    acceptCampaignAndGroupSelectorDataDnaEpic
};
